import { useEffect, useRef, useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetOrganizationUsersQuery,
  useSavedGroupsMutation,
  useDeleteSavedGroupPointsMutation,
  useFetchSavedGroupsMutation,
  useCreateAppCollectionMutation,
  useDeleteAppCollectionMutation,
} from "@/features/auth/api/authAPI";
import SortSavedModeModal from "@/shared/ui/SortSavedModeModal/SortSavedModeModal";
import { getFilterAppsState } from "@/entities/filterApps";
import Homestyles from "@/pages/Homepage/Homepage.module.scss";
import styles from "@/widgets/modalSaveMode/ui/ModalSaveMode/ModalSaveMode.module.scss";
import { getSortByCategoriesSlice } from "@/entities/sortByCategories/model/selectors/getSortByCategoriesState";
import ModalSaveModeSortBy from "@/shared/ui/ModalSaveModeSortBy/ModalSaveModeSortBy";
import { useCurrentOrganization } from "@/Hooks";
import { SavedModeMenu } from "./SavedModeMenu";
import { sortByCategoriesActions } from "@/entities/sortByCategories";
import Spinner from "@/shared/ui/Spinner/Spinner";
import { getZoomState } from "@/entities/zoom";
import ModalHidStarMenu from "@/shared/ui/ModalHidStarMenu/ModalHidStarMenu";
import {
  appsHidStarMenu,
  screensHidStarMenu,
} from "@/shared/lib/dates/dataHidStarMenu/dataHidStarMenu";
import SavedModeTabs from "./SavedModeTabs";
import ModalSaveModeGroup from "@/shared/ui/ModalSaveModeGroup/ModalSaveModeGroup";
import { produce } from "immer";
import { toast } from "react-toastify";
import Loader from "@/shared/ui/Loader/Loader";

const SavedModeBaseView = () => {
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [sortDirectionSavedMode, setSortDirectionSavedMode] = useState("desc");
  const dispatch = useDispatch();

  // Group management state
  const [selectedGroups, setSelectedGroups] = useState<Set<number>>(new Set());
  const [selectedCollections, setSelectedCollections] = useState<Set<number>>(
    new Set(),
  );
  const [modalAddGroupActive, setModalAddGroupActive] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [savedGroups, setSavedGroups] = useState([]);
  const refCreateGroup = useRef<HTMLDivElement>(null);

  // API hooks for group management
  const [deleteGroupPoint] = useDeleteSavedGroupPointsMutation();
  const [createGroup] = useSavedGroupsMutation();
  const [fetchSavedGroups] = useFetchSavedGroupsMutation();

  // API hooks for collection management
  const [createAppCollection] = useCreateAppCollectionMutation();
  const [deleteAppCollection] = useDeleteAppCollectionMutation();

  // Add a refresh function for collections
  const [refreshCollections, setRefreshCollections] = useState(0);

  const handleRefreshCollections = () => {
    setRefreshCollections((prev) => prev + 1);
  };

  const handleItemClick = (index: number) => {
    setActiveIndex(index);
  };

  const { filterSortSavedMode, userFilter } = useSelector(getFilterAppsState);
  const currentOrganization = useCurrentOrganization();

  const orderSavedMode =
    filterSortSavedMode !== ""
      ? `${sortDirectionSavedMode === "desc" ? "-" : ""}${filterSortSavedMode}`
      : "-created_at";

  const userOrderSavedMode = userFilter === null ? "" : userFilter;

  const { zoom } = useSelector(getZoomState);

  const { sortBySaveMode, saveModeOpen } = useSelector(
    getSortByCategoriesSlice,
  );

  const [selectedUser, setSelectedUser] = useState<string | null>(null);

  const { data: organizationUsers } = useGetOrganizationUsersQuery(
    currentOrganization?.uuid,
    {
      skip: !currentOrganization?.uuid,
    },
  );

  // Check if we're on collections route
  const isCollectionsRoute = () => {
    return location.pathname.includes("/saved-library/collections/");
  };

  // Group management functions
  const handleFetchSavedGroups = async () => {
    if (!currentOrganization) return;
    try {
      const result = await fetchSavedGroups(currentOrganization.uuid).unwrap();
      setSavedGroups(result.results);
    } catch (error) {
      console.error("Failed to fetch saved groups:", error);
    }
  };

  const handleDeleteGroup = async () => {
    setIsDeleting(true);
    try {
      const deletePromises = Array.from(selectedGroups).map((groupId) =>
        deleteGroupPoint(groupId).unwrap(),
      );

      await Promise.all(deletePromises);

      // Update the savedGroups state to remove all deleted groups
      setSavedGroups(
        produce((draft) => {
          return draft.filter((group) => !selectedGroups.has(group.id));
        }),
      );

      setSelectedGroups(new Set());

      // Show a success message
      toast.success(
        `Successfully deleted ${selectedGroups.size} group${selectedGroups.size > 1 ? "s" : ""}`,
      );
    } catch (error) {
      console.error("Failed to delete groups:", error);
      toast.error("Failed to delete some or all groups. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateGroup = async () => {
    setIsCreating(true);
    try {
      const newGroup = await createGroup({
        organizationUuid: currentOrganization.uuid,
        name: groupName,
        total_saved_points: 0,
        total_saved_store_screens: 0,
      }).unwrap();
      toast.success("Group created successfully!");
      await handleFetchSavedGroups();
      setTimeout(() => {
        setModalAddGroupActive(false);
        setGroupName("");
      }, 500);
    } catch (error) {
      console.error("Error creating group:", error);
      let errorMessage = "An unknown error occurred";

      if (
        error.data &&
        error.data.non_field_errors &&
        error.data.non_field_errors.length > 0
      ) {
        errorMessage = error.data.non_field_errors[0];
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      console.log("Attempting to show error toast:", errorMessage);
      toast.error(`Failed to create group: ${errorMessage}`);
    } finally {
      setIsCreating(false);
      setModalAddGroupActive(false);
    }
  };

  const handleCreateCollection = async () => {
    setIsCreating(true);
    try {
      await createAppCollection({
        name: groupName,
      }).unwrap();
      toast.success("Collection created successfully!");

      // Trigger refresh of collections
      handleRefreshCollections();

      setTimeout(() => {
        setModalAddGroupActive(false);
        setGroupName("");
      }, 500);
    } catch (error) {
      console.error("Error creating collection:", error);
      let errorMessage = "An unknown error occurred";

      if (
        error.data &&
        error.data.non_field_errors &&
        error.data.non_field_errors.length > 0
      ) {
        errorMessage = error.data.non_field_errors[0];
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      console.log("Attempting to show error toast:", errorMessage);
      toast.error(`Failed to create collection: ${errorMessage}`);
    } finally {
      setModalAddGroupActive(false);
      setIsCreating(false);
    }
  };

  const changeGroupName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGroupName(event.target.value);
  };

  const isHiddenOrStarredRoute = () => {
    const path = location.pathname;
    return (
      path.includes("/saved-library/hidden") ||
      path.includes("/saved-library/starred")
    );
  };

  // Determine if we're on the screens tab or apps tab
  const isScreensTab = () => {
    const path = location.pathname;
    const screensRoutes = [
      "/saved-library/video/",
      "/saved-library/store/",
      "/saved-library/animation/",
      "/saved-library/onboarding/",
      "/saved-library/groups/",
    ];
    return screensRoutes.some((route) => path.includes(route));
  };

  // Check if we should show SavedModeMenu (hide on groups and apps routes)
  const shouldShowSavedModeMenu = () => {
    const path = location.pathname;
    return (
      !path.includes("/saved-library/groups/") &&
      !path.includes("/saved-library/collections/") &&
      !path.includes("/saved-library/hidden/")
    );
  };

  // Check if we're on the groups route
  const isGroupsRoute = () => {
    return location.pathname.includes("/saved-library/groups/");
  };

  // Get the appropriate menu data based on active tab
  const getMenuData = () => {
    return isScreensTab() ? screensHidStarMenu : appsHidStarMenu;
  };

  useEffect(() => {
    if (saveModeOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [saveModeOpen]);

  useEffect(() => {
    if (userFilter) {
      const user = organizationUsers?.results?.find(
        (member) => member.user.id.toString() === userFilter,
      );
      if (user) {
        setSelectedUser(
          `${user.user.first_name} ${user.user.last_name}`.trim() ||
            user.user.username,
        );
      }
    }
  }, [userFilter, organizationUsers]);

  // Fetch groups when on groups route
  useEffect(() => {
    if (isGroupsRoute()) {
      handleFetchSavedGroups();
    }
  }, [currentOrganization, location.pathname]);

  const handleCloseModal = () => {
    dispatch(sortByCategoriesActions.closeSortSaveMode());
  };

  // Function to close the group modal
  const handleCloseGroupModal = () => {
    setModalAddGroupActive(false);
    setGroupName("");
  };

  // Handle clicks outside the modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalAddGroupActive &&
        refCreateGroup.current &&
        !refCreateGroup.current.contains(event.target as Node)
      ) {
        handleCloseGroupModal();
      }
    };

    if (modalAddGroupActive) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modalAddGroupActive]);

  // Get button text and handler based on route
  const getCreateButtonConfig = () => {
    if (isCollectionsRoute()) {
      return {
        text: "Create New Collection",
        handler: handleCreateCollection,
      };
    }
    return {
      text: "Create New Group",
      handler: handleCreateGroup,
    };
  };

  const createButtonConfig = getCreateButtonConfig();

  // Add collection delete handler
  const handleDeleteCollection = async () => {
    setIsDeleting(true);
    try {
      const deletePromises = Array.from(selectedCollections).map(
        (collectionId) => deleteAppCollection({ id: collectionId }).unwrap(),
      );

      await Promise.all(deletePromises);

      // Trigger refresh of collections
      handleRefreshCollections();
      setSelectedCollections(new Set());

      // Show a success message
      toast.success(
        `Successfully deleted ${selectedCollections.size} collection${selectedCollections.size > 1 ? "s" : ""}`,
      );
    } catch (error) {
      console.error("Failed to delete collections:", error);
      toast.error(
        "Failed to delete some or all collections. Please try again.",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={Homestyles.modalSaveModeContainer}>
      {currentOrganization ? (
        <div className={Homestyles.modalSaveMode}>
          <div
            ref={containerRef}
            className={`${styles.root} ${
              !location.pathname.includes("/saved-library/hidden/")
                ? styles.wrapper
                : ""
            }`}
          >
            <div className={styles.headerContent}>
              <SavedModeTabs />
              {!location.pathname.includes("/saved-library/groups/") ||
              location.pathname === "/saved-library/groups/" ? (
                <div className={styles.headerWrapper}>
                  <div className={styles.groupContainer}>
                    <ModalHidStarMenu
                      activePath={location.pathname}
                      handleItemClick={handleItemClick}
                      menuData={getMenuData()}
                    />
                  </div>
                  <div className={styles.shareContainer}>
                    {shouldShowSavedModeMenu() && (
                      <SavedModeMenu
                        activeIndex={activeIndex}
                        handleItemClick={handleItemClick}
                      />
                    )}
                    {(isGroupsRoute() || isCollectionsRoute()) && (
                      <>
                        {((isGroupsRoute() && selectedGroups.size > 0) ||
                          (isCollectionsRoute() &&
                            selectedCollections.size > 0)) && (
                          <div
                            onClick={
                              isCollectionsRoute()
                                ? handleDeleteCollection
                                : handleDeleteGroup
                            }
                            className={styles.groupDelete}
                          >
                            {isDeleting ? (
                              <Loader color="#fff" size={20} />
                            ) : (
                              <img
                                src="/img/whiteIcon/Delete.svg"
                                alt="trashImg"
                              />
                            )}
                            <p>
                              Delete{" "}
                              {isCollectionsRoute() ? "Collection" : "Group"}
                            </p>
                          </div>
                        )}
                        <div
                          onClick={() =>
                            setModalAddGroupActive(!modalAddGroupActive)
                          }
                          className={styles.groupCreate}
                        >
                          <img
                            src="/img/blackIcon/Plus.svg"
                            alt="plusGroupImg"
                          />
                          <p>{createButtonConfig.text}</p>
                        </div>
                      </>
                    )}
                    {shouldShowSavedModeMenu() && <ModalSaveModeSortBy />}
                  </div>
                </div>
              ) : null}
            </div>

            <Outlet
              context={{
                zoom: zoom,
                sortkey: orderSavedMode,
                userFilter: userOrderSavedMode,
                selectedGroups,
                setSelectedGroups,
                savedGroups,
                setSavedGroups,
                handleFetchSavedGroups,
                refreshCollections,
                handleRefreshCollections,
                selectedCollections,
                setSelectedCollections,
              }}
            />
            {saveModeOpen && (
              <SortSavedModeModal
                setSortDirection={setSortDirectionSavedMode}
                isOpen={saveModeOpen}
                onClose={handleCloseModal}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
                showSavedBy={!isHiddenOrStarredRoute()}
              />
            )}

            {/* Group/Collection creation modal */}
            {modalAddGroupActive && (
              <ModalSaveModeGroup
                ref={refCreateGroup}
                changeGroupName={changeGroupName}
                groupName={groupName}
                handleCreateGroup={createButtonConfig.handler}
                isCollection={isCollectionsRoute()}
                isCreating={isCreating}
              />
            )}
          </div>
        </div>
      ) : (
        <Spinner />
      )}
    </div>
  );
};

export { SavedModeBaseView };
